import { MouseEvent, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import axios from 'common/AxiosAuth';
import { Button, DescriptionBar, Grid, InfoMessage, Input, Text } from 'components';
import { StatusBadge, Status } from 'components/StatusBadge';
import { Message } from 'types/message';
import { CommandingGridData, CommandingStatusKey, PreScriptCommandItem } from './type';
import { COMMANDING_GRID_COLUMNS, COMMANDING_STATUS_MAP } from './utils';

dayjs.extend(utc);


function parseUtcDate(value?: string) {
  return dayjs.utc(value ?? '');
}

function mapScheduleStatusToCommandingStatus(status: string, startTime: string, endTime: string): CommandingStatusKey {
  const now = dayjs.utc();
  const start = parseUtcDate(startTime);
  const end = parseUtcDate(endTime);

  if (!start.isValid() || !end.isValid()) {
    return 'UNKNOWN';
  }

  if (status === 'PROCESS' || (start.isBefore(now) && end.isAfter(now))) {
    return 'LIVE_COMMANDING';
  }
  if (status === 'COMPLETE' || end.isBefore(now)) {
    return 'CONTACT_ENDED';
  }
  if (status === 'SCHEDULED' || start.isAfter(now)) {
    return 'PRE_SCRIPT';
  }
  return 'UNKNOWN';
}

function mapCancelStatusIdToName(cancelStatus: string) {
  switch (cancelStatus) {
    case 'CANCEL': return 'Cancel';
    case 'TOBEREFUNDED': return 'Toberefunded';
    case 'TO_BE_REFUNDED': return 'TO_BE_REFUNDED';
    case 'REFUNDED': return 'Refunded';
    case 'NONE': return 'None';
    default: return cancelStatus || 'None';
  }
}

function formatCountdown(startTime: string, endTime: string) {
  const now = dayjs.utc();
  const start = parseUtcDate(startTime);
  const end = parseUtcDate(endTime);

  if (!start.isValid() || !end.isValid()) {
    return '-';
  }

  if (start.isAfter(now)) {
    const diffMinutes = start.diff(now, 'minute');
    if (diffMinutes >= 60) {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return `${hours}h ${minutes}m`;
    }
    if (diffMinutes > 0) {
      return `${diffMinutes}m`;
    }
    const diffSeconds = start.diff(now, 'second');
    return diffSeconds > 0 ? `${diffSeconds}s` : 'Now';
  }

  if (end.isAfter(now)) {
    return 'In Progress';
  }

  return 'Ended';
}

function formatDateTime(value: string) {
  return value || '-';
}

function formatAvailableTime(value: number) {
  if (!Number.isFinite(value)) {
    return '-';
  }
  return `${value} sec`;
}

function formatAltitude(value: number) {
  if (!Number.isFinite(value)) {
    return '-';
  }
  return `${value}º`;
}

function formatSecondsLabel(value: number) {
  if (!Number.isFinite(value) || value < 0) {
    return '0 sec';
  }
  return `${Math.round(value)} sec`;
}

const Commanding = () => {
  const [gridData, setGridData] = useState<CommandingGridData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<CommandingGridData | null>(null);
  const [hexCommand, setHexCommand] = useState<string>('');
  const [executeAtOffsetSec, setExecuteAtOffsetSec] = useState<number | ''>(0);
  const [commandItems, setCommandItems] = useState<PreScriptCommandItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [commandError, setCommandError] = useState<string | null>(null);
  const [hexCommandError, setHexCommandError] = useState<string | null>(null);
  const [messageArray, setMessageArray] = useState<Message[]>([]);
  const [messageArrayCount, setMessageArrayCount] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingHexValue, setEditingHexValue] = useState<string>('');
  const [editingHexError, setEditingHexError] = useState<string | null>(null);
  const [editingOffsetValue, setEditingOffsetValue] = useState<number | ''>(0);
  const [editingOffsetError, setEditingOffsetError] = useState<string | null>(null);
  const [hasExistingScript, setHasExistingScript] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const handleDeleteMessage = useCallback((index: number) => {
    setMessageArray((prev) => prev.filter((message) => message.index !== index));
  }, []);

  const pageSize = 10;

  const handleCheckedRowsChange = useCallback((value: SetStateAction<number[]>) => {
    setCheckedRows((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      if (!next || next.length === 0) {
        return [];
      }
      const last = next[next.length - 1];
      return [last];
    });
  }, []);

  useEffect(() => {
    const fetchCommandingList = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get('/api/schedule/search', {
          params: {
            page: currentPage - 1,
            size: pageSize,
            sort: 'startTime,ASC',
            type: 'UPLINK'
          }
        });
        const { content = [], totalElements = 0 } = res.data ?? {};

        const nextGridData: CommandingGridData[] = content.map((item: any) => {
          const rawStartTime = item.startTime ?? item.StartTime ?? '';
          const rawEndTime = item.endTime ?? item.EndTime ?? '';
          const startTimeFormatted = formatDateTime(rawStartTime);
          const endTimeFormatted = formatDateTime(rawEndTime);
          const availableTimeValue = Number(item.availableTime ?? item.AvailableTime ?? 0);
          const maxElevationValue = Number(item.maxElevationDegrees ?? item.MaxElevation ?? 0);
          const commandingStatus = mapScheduleStatusToCommandingStatus(
            item.status ?? item.Status ?? '',
            rawStartTime,
            rawEndTime
          );
          const scheduleId = item.id ?? item.scheduleId ?? item.linkId ?? 0;
          const cancelStatus = mapCancelStatusIdToName(item.cancelStatus ?? 'NONE');

          return {
            id: scheduleId,
            linkId: item.linkId ?? 0,
            contactProfileId: item.contactProfileId ?? 0,
            linkName: item.linkName ?? item.satelliteName ?? '-',
            contactProfileName: item.contactProfileName ?? '-',
            startTime: startTimeFormatted,
            endTime: endTimeFormatted,
            availableTime: availableTimeValue,
            maxElevationDegrees: maxElevationValue,
            rawStatus: item.status ?? item.Status ?? 'UNKNOWN',
            commandingStatus,
            countdownLabel: formatCountdown(rawStartTime, rawEndTime),
            hasPreScript: false, // 초기값, 나중에 업데이트
            cancelStatus
          };
        });

        // 각 스케줄에 대해 PreScript 존재 여부 확인
        const checkPreScripts = async () => {
          const preScriptChecks = await Promise.all(
            nextGridData.map(async (schedule) => {
              if (!schedule.id || schedule.id <= 0) {
                return { id: schedule.id, hasPreScript: false };
              }
              try {
                const res = await axios.get(`/api/mission-control/${schedule.id}`);
                const scriptData = res.data;
                const hasPreScript = scriptData && scriptData.items && Array.isArray(scriptData.items) && scriptData.items.length > 0;
                return { id: schedule.id, hasPreScript };
              } catch (error) {
                // 에러 발생 시 PreScript 없음으로 처리
                return { id: schedule.id, hasPreScript: false };
              }
            })
          );

          // PreScript 존재 여부 업데이트
          const updatedGridData = nextGridData.map((schedule) => {
            const check = preScriptChecks.find((c) => c.id === schedule.id);
            return {
              ...schedule,
              hasPreScript: check?.hasPreScript ?? false
            };
          });

          setGridData(updatedGridData);
        };

        // 초기 데이터 설정 후 PreScript 확인
        setGridData(nextGridData);
        checkPreScripts();
        console.log('[Commanding] Grid data:', nextGridData);
        setTotalPages(Math.ceil(totalElements / pageSize));
        const firstSelectableIndex = nextGridData.findIndex(
          (item) => item.commandingStatus !== 'CONTACT_ENDED' && item.cancelStatus !== 'Cancel'
        );
        if (firstSelectableIndex >= 0) {
          setCheckedRows([firstSelectableIndex]);
          const initial = nextGridData[firstSelectableIndex];
          setSelectedSchedule(initial);
          
          // initial.id 유효성 검사 후에만 API 호출
          if (initial && initial.id && typeof initial.id === 'number' && initial.id > 0) {
            // 모든 스케줄에 대해 스크립트가 있는지 확인
            const loadScript = async () => {
              try {
                const res = await axios.get(`/api/mission-control/${initial.id}`);
                const scriptData = res.data;

                if (scriptData && scriptData.items && Array.isArray(scriptData.items) && scriptData.items.length > 0) {
                  const loadedItems: PreScriptCommandItem[] = scriptData.items
                    .map((item: any, index: number) => ({
                      sequenceOrder: item.sequenceOrder ?? index + 1,
                      executeAtOffsetSec: item.executeAtOffsetSec ?? 0,
                      hexCommand: item.hexCommand ?? ''
                    }))
                    .sort((a: PreScriptCommandItem, b: PreScriptCommandItem) => a.executeAtOffsetSec - b.executeAtOffsetSec);
                  
                  setCommandItems(loadedItems);
                  setHasExistingScript(true);
                  setIsEditMode(true);
                } else {
                  // 스크립트가 없거나 빈 배열인 경우 (백엔드에서 빈 값으로 리턴)
                  setCommandItems([]);
                  setHasExistingScript(false);
                  setIsEditMode(false);
                }
              } catch (error: any) {
                // 예상치 못한 에러만 처리
                console.error('[Commanding] Initial load - Failed to load existing script:', error);
                setCommandItems([]);
                setHasExistingScript(false);
                setIsEditMode(false);
              }
            };
            loadScript();
          } else {
            // 유효하지 않은 ID인 경우
            setCommandItems([]);
            setHasExistingScript(false);
            setIsEditMode(false);
          }
        } else {
          setCheckedRows([]);
          setSelectedSchedule(null);
          setHasExistingScript(false);
          setIsEditMode(false);
        }
      } catch (error) {
        console.error('Failed to fetch commanding list:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommandingList();
  }, [currentPage, pageSize]);

  useEffect(() => {
    const filtered = checkedRows.filter(
      (index) => gridData[index]?.commandingStatus !== 'CONTACT_ENDED' && gridData[index]?.cancelStatus !== 'Cancel'
    );
    if (filtered.length !== checkedRows.length) {
      setCheckedRows(filtered);
      if (filtered.length === 0) {
        setSelectedSchedule(null);
        setHexCommand('');
        setExecuteAtOffsetSec(0);
        setCommandItems([]);
        setCommandError(null);
        setMessageArray([]);
        setMessageArrayCount(0);
        setHasExistingScript(false);
        setEditingIndex(null);
        setEditingHexValue('');
        setIsEditMode(false);
      }
      return;
    }
    if (!filtered.length) {
      setSelectedSchedule(null);
      setHexCommand('');
      setExecuteAtOffsetSec(0);
      setCommandItems([]);
      setCommandError(null);
      setMessageArray([]);
      setMessageArrayCount(0);
      setHasExistingScript(false);
      setEditingIndex(null);
      setEditingHexValue('');
      setIsEditMode(false);
      return;
    }
    const selectedIndex = filtered[0];
    if (gridData[selectedIndex]) {
      const schedule = gridData[selectedIndex];
      setSelectedSchedule(schedule);
      setHexCommand('');
      setExecuteAtOffsetSec(0);
      setCommandError(null);
      setMessageArray([]);
      setMessageArrayCount(0);
      setEditingIndex(null);
      setEditingHexValue('');
      
      // schedule.id 유효성 검사 후에만 API 호출
      if (schedule && schedule.id && typeof schedule.id === 'number' && schedule.id > 0) {
        // 모든 스케줄에 대해 스크립트가 있는지 확인
        const loadScript = async () => {
          try {
            const res = await axios.get(`/api/mission-control/${schedule.id}`);
            const scriptData = res.data;

            if (scriptData && scriptData.items && Array.isArray(scriptData.items) && scriptData.items.length > 0) {
              const loadedItems: PreScriptCommandItem[] = scriptData.items
                .map((item: any, index: number) => ({
                  sequenceOrder: item.sequenceOrder ?? index + 1,
                  executeAtOffsetSec: item.executeAtOffsetSec ?? 0,
                  hexCommand: item.hexCommand ?? ''
                }))
                .sort((a: PreScriptCommandItem, b: PreScriptCommandItem) => a.executeAtOffsetSec - b.executeAtOffsetSec);

              setCommandItems(loadedItems);
              setHasExistingScript(true);
              setIsEditMode(true);
            } else {
              // 스크립트가 없거나 빈 배열인 경우 (백엔드에서 빈 값으로 리턴)
              setCommandItems([]);
              setHasExistingScript(false);
              setIsEditMode(false);
            }
          } catch (error: any) {
            // 예상치 못한 에러만 처리
            console.error('[Commanding] Failed to load existing script:', error);
            setCommandItems([]);
            setHasExistingScript(false);
            setIsEditMode(false);
          }
        };
        loadScript();
      } else {
        // 유효하지 않은 ID인 경우
        setCommandItems([]);
        setHasExistingScript(false);
        setIsEditMode(false);
      }
    }
  }, [checkedRows, gridData]);

  const schedulerTitle = useMemo(() => {
    if (!selectedSchedule) {
      return 'Pre-Script Command Scheduler';
    }
    return `${selectedSchedule.linkName} - Pre-Script Command Scheduler`;
  }, [selectedSchedule]);

  const availableWindow = selectedSchedule?.availableTime ?? 0;
  const nextSequenceOrder = commandItems.length + 1;
  const timelineLabels = useMemo(() => {
    if (!availableWindow || availableWindow <= 0) {
      return ['0 sec'];
    }
    const segments = 6;
    const interval = availableWindow / segments;
    return Array.from({ length: segments + 1 }, (_, index) =>
      formatSecondsLabel(Math.min(availableWindow, Math.round(interval * index)))
    );
  }, [availableWindow]);

  const executeOffsetValue =
    typeof executeAtOffsetSec === 'number' ? executeAtOffsetSec : Number(executeAtOffsetSec ?? 0);
  const offsetPercent =
    availableWindow > 0 ? Math.min(100, Math.max(0, (executeOffsetValue / availableWindow) * 100)) : 0;

  useEffect(() => {
    if (availableWindow <= 0) {
      if (executeAtOffsetSec !== 0) {
        setExecuteAtOffsetSec(0);
      }
      return;
    }
    if (executeAtOffsetSec === '') {
      return;
    }
    const clamped = Math.min(availableWindow, Math.max(0, executeAtOffsetSec));
    if (clamped !== executeAtOffsetSec) {
      setExecuteAtOffsetSec(clamped);
    }
  }, [executeAtOffsetSec, availableWindow]);

  const handleTimelineClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!availableWindow || availableWindow <= 0) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    const seconds = Math.round(Math.min(Math.max(ratio, 0), 1) * availableWindow);
    setExecuteAtOffsetSec(seconds);
    setCommandError(null);
    if (editingIndex !== null) {
      setEditingIndex(null);
      setEditingHexValue('');
    }
  };

  // 16진수 값 검증 함수 (형식 검증만)
  const validateHexCommand = (hexValue: string): { isValid: boolean; error?: string } => {
    const trimmed = hexValue.trim();
    if (!trimmed) {
      return { isValid: false, error: 'Hex command cannot be empty.' };
    }
    // 16진수 형식 검증 (0-9, A-F, a-f만 허용)
    if (!/^[0-9A-Fa-f]+$/.test(trimmed)) {
      return { isValid: false, error: 'Hex command must contain only hexadecimal characters (0-9, A-F).' };
    }
    return { isValid: true };
  };

  const handleAddCommand = () => {
    if (!selectedSchedule || !hexCommand) {
      return;
    }
    // 16진수 값 검증
    const validation = validateHexCommand(hexCommand);
    if (!validation.isValid) {
      setHexCommandError(validation.error || 'Invalid hex command.');
      return;
    }
    setHexCommandError(null);
    if (commandItems.length >= 8) {
      setCommandError('You can add up to 8 commands only.');
      return;
    }
    const offsetValue =
      typeof executeAtOffsetSec === 'number' ? executeAtOffsetSec : Number(executeAtOffsetSec ?? 0);
    const clampedOffset = Math.min(availableWindow, Math.max(0, offsetValue));
    if (commandItems.some((item) => item.executeAtOffsetSec === clampedOffset)) {
      setCommandError('A command already exists for this offset time.');
      return;
    }
    const lastOffset = commandItems.length > 0 ? commandItems[commandItems.length - 1].executeAtOffsetSec : 0;
    if (clampedOffset < lastOffset) {
      setCommandError('Offset must be greater than or equal to the previous command time.');
      return;
    }
    setCommandItems((prev) => [
      ...prev,
      {
        sequenceOrder: prev.length + 1,
        executeAtOffsetSec: clampedOffset,
        hexCommand: hexCommand.trim()
      }
    ]);
    setHexCommand('');
    setExecuteAtOffsetSec(0);
    setCommandError(null);
    setHexCommandError(null);
    setEditingIndex(null);
    setEditingHexValue('');
  };

  const handleRemoveCommand = (sequenceOrder: number) => {
    setCommandItems((prev) =>
      prev
        .filter((item) => item.sequenceOrder !== sequenceOrder)
        .map((item, index) => ({
          ...item,
          sequenceOrder: index + 1
        }))
    );
    setCommandError(null);
    setEditingIndex(null);
    setEditingHexValue('');
  };

  const handleSave = async () => {
    if (!selectedSchedule) {
      return;
    }
    if (editingIndex !== null) {
      setCommandError('Finish editing the selected command before saving.');
      return;
    }
    if (hexCommand) {
      setCommandError('Please add the current command before saving.');
      return;
    }
    // 수정 모드가 아닐 때만 기존 스크립트 체크
    if (!isEditMode && hasExistingScript && commandItems.length === 0) {
      setCommandError('A pre-script already exists for this schedule.');
      return;
    }
    if (!commandItems.length) {
      setCommandError('No telecommands to send.');
      return;
    }

    // 저장 전 모든 hexCommand 값 검증
    for (const item of commandItems) {
      const validation = validateHexCommand(item.hexCommand);
      if (!validation.isValid) {
        const errorMsg = `Invalid hex command in sequence #${item.sequenceOrder}: ${validation.error}`;
        setCommandError(errorMsg);
        const errorIndex = messageArrayCount;
        setMessageArray((prev) => [
          ...prev,
          {
            index: errorIndex,
            text: errorMsg,
            icon: undefined
          }
        ]);
        setMessageArrayCount((prev) => prev + 1);
        setTimeout(() => handleDeleteMessage(errorIndex), 4000);
        return;
      }
    }

    setIsSaving(true);
    try {
      const requestData = {
        scriptName: `${selectedSchedule.linkName ?? 'Pre Script'}`,
        items: commandItems
      };

      if (isEditMode) {
        await axios.patch(`/api/mission-control/${selectedSchedule.id}`, requestData);
      } else {
        await axios.post(`/api/mission-control/${selectedSchedule.id}`, requestData);
      }
      
      const wasEditMode = isEditMode;
      
      // 저장 성공 후 저장된 스크립트를 다시 불러오기
      try {
        const res = await axios.get(`/api/mission-control/${selectedSchedule.id}`);
        const scriptData = res.data;

        if (scriptData && scriptData.items && Array.isArray(scriptData.items) && scriptData.items.length > 0) {
          const loadedItems: PreScriptCommandItem[] = scriptData.items
            .map((item: any, index: number) => ({
              sequenceOrder: item.sequenceOrder ?? index + 1,
              executeAtOffsetSec: item.executeAtOffsetSec ?? 0,
              hexCommand: item.hexCommand ?? ''
            }))
            .sort((a: PreScriptCommandItem, b: PreScriptCommandItem) => a.executeAtOffsetSec - b.executeAtOffsetSec);

          setCommandItems(loadedItems);
          setHasExistingScript(true);
          setIsEditMode(true);
        } else {
          setCommandItems([]);
          setHasExistingScript(false);
          setIsEditMode(false);
        }
      } catch (error) {
        // 스크립트 불러오기 실패 시 현재 저장한 내용 유지
        console.error('Failed to reload script after save:', error);
        setHasExistingScript(true);
        setIsEditMode(true);
      }
      
      setHexCommand('');
      setExecuteAtOffsetSec(0);
      setCommandError(null);
      setEditingIndex(null);
      setEditingHexValue('');
      const successIndex = messageArrayCount;
      setMessageArray((prev) => [
        ...prev,
        {
          index: successIndex,
          text: wasEditMode ? 'Telecommands have been updated successfully.' : 'Telecommands have been scheduled successfully.',
          icon: undefined
        }
      ]);
      setMessageArrayCount((prev) => prev + 1);
      setTimeout(() => handleDeleteMessage(successIndex), 3000);
      
      // Grid 데이터 업데이트: PreScript 상태 갱신
      setGridData((prevData) =>
        prevData.map((item) =>
          item.id === selectedSchedule.id
            ? { ...item, hasPreScript: true }
            : item
        )
      );
    } catch (error: any) {
      console.error('Failed to save pre-script command:', error);
      const errorIndex = messageArrayCount;
      let errorMessage = 'Failed to save telecommands. Please try again.';
      
      // 백엔드 에러 메시지 확인
      if (error?.response?.data?.message) {
        const backendMessage = error.response.data.message;
        if (backendMessage.includes('이미 저장된 스크립트가 존재합니다')) {
          errorMessage = 'A pre-script already exists for this schedule.';
        } else if (backendMessage.includes('NumberFormatException') || backendMessage.includes('For input string')) {
          errorMessage = 'Invalid hex command format. Please check the hexadecimal values.';
        } else {
          errorMessage = backendMessage;
        }
      } else if (error?.response?.status === 400) {
        errorMessage = 'Invalid request. Please check the hex command values.';
      }
      
      setCommandError(errorMessage);
      setMessageArray((prev) => [
        ...prev,
        {
          index: errorIndex,
          text: errorMessage,
          icon: undefined
        }
      ]);
      setMessageArrayCount((prev) => prev + 1);
      setTimeout(() => handleDeleteMessage(errorIndex), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCommand = (sequenceOrder: number) => {
    const item = commandItems.find((command) => command.sequenceOrder === sequenceOrder);
    if (!item) {
      return;
    }
    setEditingIndex(sequenceOrder);
    setEditingHexValue(item.hexCommand);
    setEditingOffsetValue(item.executeAtOffsetSec);
    setCommandError(null);
    setEditingHexError(null);
    setEditingOffsetError(null);
  };

  const handleEditSave = (sequenceOrder: number) => {
    if (!editingHexValue.trim()) {
      setEditingHexError('Hex command cannot be empty.');
      return;
    }
    // 16진수 값 검증
    const validation = validateHexCommand(editingHexValue);
    if (!validation.isValid) {
      setEditingHexError(validation.error || 'Invalid hex command.');
      return;
    }
    
    // Offset 값 검증
    const offsetValue = typeof editingOffsetValue === 'number' ? editingOffsetValue : Number(editingOffsetValue ?? 0);
    if (offsetValue < 0) {
      setEditingOffsetError('Offset must be 0 or greater.');
      return;
    }
    if (offsetValue > availableWindow) {
      setEditingOffsetError(`Offset must be less than or equal to ${availableWindow} sec.`);
      return;
    }
    
    // 다른 항목과 Offset 중복 확인 (현재 편집 중인 항목 제외)
    const hasDuplicateOffset = commandItems.some(
      (item) => item.sequenceOrder !== sequenceOrder && item.executeAtOffsetSec === offsetValue
    );
    if (hasDuplicateOffset) {
      setEditingOffsetError('A command already exists for this offset time.');
      return;
    }
    
    const clampedOffset = Math.min(availableWindow, Math.max(0, offsetValue));
    
    setCommandItems((prev) =>
      prev.map((item) =>
        item.sequenceOrder === sequenceOrder
          ? { ...item, hexCommand: editingHexValue.trim(), executeAtOffsetSec: clampedOffset }
          : item
      ).sort((a, b) => a.executeAtOffsetSec - b.executeAtOffsetSec)
      .map((item, index) => ({
        ...item,
        sequenceOrder: index + 1
      }))
    );
    setEditingIndex(null);
    setEditingHexValue('');
    setEditingOffsetValue(0);
    setEditingHexError(null);
    setEditingOffsetError(null);
    setCommandError(null);
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingHexValue('');
    setEditingOffsetValue(0);
    setEditingHexError(null);
    setEditingOffsetError(null);
    setCommandError(null);
  };

  return (
    <>
      <div className="flex h-full w-full flex-col">
      <DescriptionBar
        className="md:px-6"
          title="Commanding"
          description="Manage uplink telecommands and schedule pre-scripted commands for upcoming contacts."
        />

        <div className="flex flex-1 flex-col gap-4 px-6 pb-6 pt-6">
          {isLoading ? (
            <div className="flex h-64 w-full items-center justify-center rounded border border-dashed border-border-primary">
              <Text size="body1" weight="medium">
                Loading uplink commanding list...
              </Text>
            </div>
          ) : (
          <Grid
              className="max-w-full overflow-y-auto rounded border border-border-primary"
              gridColumns={COMMANDING_GRID_COLUMNS}
              gridData={gridData.map((item) => ({
                ...item,
                availableTime: formatAvailableTime(item.availableTime),
                maxElevationDegrees: formatAltitude(item.maxElevationDegrees),
                preScriptStatus: item.hasPreScript ? 'done' : 'pending'
              }))}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            isDisabled={(row) => row.commandingStatus === 'CONTACT_ENDED' || row.cancelStatus === 'Cancel'}
              options={{
                useCheckbox: true,
                hideSelectAllCheckbox: true,
                rowClassName: (row) =>
                  row.commandingStatus === 'CONTACT_ENDED' || row.cancelStatus === 'Cancel'
                    ? 'text-gray-900 [&_td_p]:text-gray-900 [&_td_span]:text-gray-900'
                    : undefined,
                usePagination: {
                  use: true,
                  maxPage: totalPages
                },
                useSort: {
                  use: true,
                  sortKeys: ['linkName', 'contactProfileName', 'startTime', 'endTime']
                },
                customCell: {
                  commandingStatus: (_, row) => {
                    const statusKey = row.commandingStatus;
                    return (
                      <StatusBadge value={statusKey} statusMap={COMMANDING_STATUS_MAP as Record<string, Status>} />
                    );
                  },
                  preScriptStatus: (_, row) => {
                    const hasPreScript = row.hasPreScript ?? false;
                    if (hasPreScript) {
                      return (
                        <div className="flex h-6 w-fit items-center rounded-full border px-3 bg-[#E6FEED] border-[#27B72D] text-[#27B72D]">
                          <span className="text-nowrap text-small font-regular">Done</span>
                        </div>
                      );
                    }
                    return (
                      <div className="flex h-6 w-fit items-center rounded-full border px-3 bg-[#F5F5F5] border-[#B0B0B0] text-[#707070]">
                        <span className="text-nowrap text-small font-regular">Pending</span>
                      </div>
                    );
                  }
                }
              }}
              checkedRows={checkedRows}
            setCheckedRows={handleCheckedRowsChange}
            />
          )}

          <div className="flex flex-col gap-4 rounded border border-border-primary bg-bg-layer1 p-6 lg:flex-row">
            <div className="flex flex-col gap-3 lg:w-[240px]">
              <Text size="body1" weight="semibold">
                Command Mode
              </Text>
              <Button
                className="h-8 w-full"
                text="Pre Script"
                type="blue"
                size="large"
                disabled={!selectedSchedule || selectedSchedule.cancelStatus === 'Cancel'}
              />
              <div className="rounded border border-border-primary bg-bg-layer2 p-4">
                <Text size="body2" weight="medium" className="text-text-secondary">
                  Selected Contact
                </Text>
                <Text size="body1" weight="semibold" className="mt-2">
                  {selectedSchedule?.linkName ?? '-'}
                </Text>
                <div className="mt-2 flex flex-col gap-1 text-body2">
                  <span>Ground Station: {selectedSchedule?.contactProfileName ?? '-'}</span>
                  <span>AOS : {selectedSchedule?.startTime ?? '-'}</span>
                  <span>LOS : {selectedSchedule?.endTime ?? '-'}</span>
                  <span>Available: {selectedSchedule ? formatAvailableTime(selectedSchedule.availableTime) : '-'}</span>
                </div>
              </div>
            </div>

            <div className="flex-1 rounded border border-border-primary bg-bg-layer2 p-6">
              <div className="flex flex-col gap-4">
                <Text size="body1" weight="semibold">
                  {schedulerTitle}
                </Text>
                <div className="flex flex-row items-center justify-between rounded border border-border-primary bg-bg-layer1 px-4 py-3">
                  <Text size="body2" weight="medium">
                    Sequence Order
                  </Text>
                  <Text size="body2" weight="semibold">
                    {nextSequenceOrder}
                  </Text>
                </div>
                <Input
                  label="Execute Offset (sec)"
                  type="number"
                  value={executeAtOffsetSec}
                  setValue={setExecuteAtOffsetSec}
                  placeholder="0"
                  errorMessage={commandError ?? undefined}
                />
                <div className="flex flex-col gap-2">
                  <label className="text-body2 font-medium text-text-secondary">Hex Command</label>
                  <div className="flex items-center gap-2">
                    <div className="rounded bg-bg-layer2 py-[6px] text-body2 font-semibold text-text-primary">
                      0x
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="Enter a hex value (e.g. 7FFFFFFF)"
                        value={hexCommand}
                        setValue={(value) => {
                          setHexCommand(value);
                          setHexCommandError(null);
                        }}
                        errorMessage={hexCommandError ?? undefined}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-row flex-wrap gap-2 self-end">
                  <Button
                    className="h-8"
                    text="Add Command"
                    type="secondary"
                    size="large"
                    disabled={!selectedSchedule || !hexCommand || editingIndex !== null || selectedSchedule.cancelStatus === 'Cancel'}
                    onClick={handleAddCommand}
                  />
                  <Button
                    className="h-8"
                    text="Reset Input"
                    type="secondary"
                    size="large"
                    onClick={() => {
                      setHexCommand('');
                      setExecuteAtOffsetSec(0);
                      setCommandError(null);
                      setHexCommandError(null);
                      setEditingIndex(null);
                      setEditingHexValue('');
                      setEditingOffsetValue(0);
                      setEditingHexError(null);
                      setEditingOffsetError(null);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-2 rounded border border-border-primary bg-bg-layer1 p-4">
                  <Text size="body2" weight="medium" className="text-text-secondary">
                    Commands to Send
                  </Text>
                  {commandItems.length > 0 ? (
                    commandItems.map((item) => (
                      <div
                        key={`command_item_${item.sequenceOrder}`}
                        className="flex flex-col gap-2 rounded border border-border-primary bg-white p-3 md:flex-row md:items-center md:justify-between"
                      >
                        <div className="flex flex-wrap items-center gap-4">
                          <Text size="body2" weight="semibold">
                            #{item.sequenceOrder}
                          </Text>
                          {editingIndex === item.sequenceOrder ? (
                            <div className="flex gap-2">
                              <div className="flex items-center gap-2">
                                <span className="text-body2 font-medium text-text-secondary">Offset:</span>
                                <div className="w-32">
                                  <Input
                                    type="number"
                                    placeholder="0"
                                    value={editingOffsetValue}
                                    setValue={(value) => {
                                      setEditingOffsetValue(value);
                                      setEditingOffsetError(null);
                                    }}
                                    errorMessage={editingOffsetError ?? undefined}
                                  />
                                </div>
                                <span className="text-body2 font-medium text-text-secondary">sec</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-body2 font-medium text-text-secondary">Hex:</span>
                                <div className="flex items-center gap-2">
                                  <div className="rounded bg-bg-layer2 py-[4px] px-2 text-body2 font-semibold text-text-primary">
                                    0x
                                  </div>
                                  <div className="flex-1">
                                    <Input
                                      placeholder="Enter a hex value"
                                      value={editingHexValue}
                                      setValue={(value) => {
                                        setEditingHexValue(value);
                                        setEditingHexError(null);
                                      }}
                                      errorMessage={editingHexError ?? undefined}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <Text size="body2" weight="medium" className="text-text-secondary">
                                Offset: {item.executeAtOffsetSec} sec
                              </Text>
                              <Text size="body2" weight="medium" className="text-text-secondary">
                                Hex:{' '}
                                <span className="font-semibold text-text-primary">{item.hexCommand}</span>
                              </Text>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {editingIndex === item.sequenceOrder ? (
                            <>
                              <Button
                                className="h-8"
                                text="Save"
                                type="secondary"
                                size="small"
                                onClick={() => handleEditSave(item.sequenceOrder)}
                              />
                              <Button
                                className="h-8"
                                text="Cancel"
                                type="secondary"
                                size="small"
                                onClick={handleEditCancel}
                              />
                            </>
                          ) : (
                            <Button
                              className="h-8"
                              text="Edit"
                              type="secondary"
                              size="small"
                              onClick={() => handleEditCommand(item.sequenceOrder)}
                            />
                          )}
                          <Button
                            className="h-8 bg-red-600 border-red-600"
                            text="Remove"
                            size="small"
                            onClick={() => handleRemoveCommand(item.sequenceOrder)}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <Text size="body2" weight="regular" className="text-text-secondary">
                      No commands added yet.
                    </Text>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button
                    className="h-8"
                    text={isEditMode ? (isSaving ? 'Updating...' : 'Update Commands') : (isSaving ? 'Saving...' : 'Save Commands')}
                    type="blue"
                    size="large"
                    disabled={
                      !selectedSchedule ||
                      (commandItems.length === 0 && !hexCommand) ||
                      isSaving ||
                      editingIndex !== null ||
                      selectedSchedule.cancelStatus === 'Cancel'
                    }
                    onClick={handleSave}
                  />
                </div>
                <div className="mt-2 flex flex-col gap-3">
                  <div className="flex flex-row items-center justify-between text-small text-text-secondary">
                    {timelineLabels.map((label, index) => (
                      <span key={`timeline_label_${index}`}>{label}</span>
                    ))}
                  </div>
                  <div
                    className="relative h-14 cursor-pointer rounded border border-dashed border-border-primary bg-bg-layer1"
                    onClick={handleTimelineClick}
                  >
                    {commandItems.map((item) => {
                      const percent = availableWindow > 0 ? (item.executeAtOffsetSec / availableWindow) * 100 : 0;
                      return (
                        <div
                          key={`command_marker_${item.sequenceOrder}`}
                          className="absolute top-0 flex h-full -translate-x-1/2 flex-col items-center justify-start"
                          style={{ left: `${Math.min(100, Math.max(0, percent))}%` }}
                        >
                          <div className="mt-1 rounded-full bg-blue-500 px-2 py-[2px] text-[10px] font-semibold text-white shadow-sm">
                            #{item.sequenceOrder}
                          </div>
                          <div className="mt-auto h-full w-[2px] bg-blue-400/60" />
                        </div>
                      );
                    })}
                    <div
                      className="absolute top-0 h-full w-[2px] bg-blue-500"
                      style={{ left: `calc(${offsetPercent}% - 1px)` }}
                    />
                    <div
                      className="absolute top-1/2 -translate-y-1/2 rounded border border-blue-400 bg-blue-200/60 px-2 py-1 text-small font-medium text-blue-700"
                      style={{
                        left: `calc(${offsetPercent}% - 40px)`
                      }}
                    >
                      {`${executeOffsetValue}s`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <InfoMessage messageArray={messageArray} onClickDelete={handleDeleteMessage} />
    </>
  );
};

export default Commanding;